import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api } from '../services/api';
import { websocketService } from '../services/websocket';
import toast from 'react-hot-toast';

interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  avatar?: string;
  is_active: boolean;
  is_admin: boolean;
  email_verified_at?: string;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  phone?: string;
  cpf_cnpj: string;
  company_name?: string;
  cep: string;
  address: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/auth/me');
        if (response.data.success) {
          const userData = response.data.data.user;
          setUser(userData);
          
          // Conectar WebSocket após autenticação
          websocketService.connect(token, userData.id);
          websocketService.requestNotificationPermission();
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      if (response.data.success) {
        const { user: userData, token, refreshToken } = response.data.data;
        
        // Store tokens
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);
        
        // Set user data
        setUser(userData);
        
        // Conectar WebSocket após login
        websocketService.connect(token, userData.id);
        websocketService.requestNotificationPermission();
        
        toast.success('Login realizado com sucesso!');
        return true;
      } else {
        toast.error(response.data.error || 'Erro no login');
        return false;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Erro no login';
      toast.error(errorMessage);
      return false;
    }
  };

  const register = async (data: RegisterData): Promise<boolean> => {
    try {
      const response = await api.post('/auth/register', data);

      if (response.data.success) {
        // Se retornou dados de usuário e token, faz login automático
        if (response.data.data.user && response.data.data.token) {
          const { user: userData, token, refreshToken } = response.data.data;

          // Store tokens
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);

          // Set user data
          setUser(userData);

          // Conectar WebSocket após registro com login automático
          websocketService.connect(token, userData.id);
          websocketService.requestNotificationPermission();

          toast.success(response.data.message || 'Conta criada e login realizado com sucesso!');
        } else {
          // Cadastro sem login automático
          toast.success(response.data.message || 'Conta criada com sucesso! Faça login para continuar.');
        }
        return true;
      } else {
        toast.error(response.data.error || 'Erro no cadastro');
        return false;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Erro no cadastro';
      toast.error(errorMessage);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Desconectar WebSocket
      websocketService.disconnect();
      
      // Clear tokens and user data
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setUser(null);
      toast.success('Logout realizado com sucesso!');
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export type { User, RegisterData };